import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const Login = () => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isStaffLogin, setIsStaffLogin] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = isStaffLogin ? '/api/tickets/staff/login' : '/api/auth/login';
      const response = await axios.post(`${API_BASE_URL}${endpoint}`, credentials);
      
      if (isStaffLogin) {
        const { token, staff } = response.data;
        localStorage.setItem('token', token);
        localStorage.setItem('staff', JSON.stringify(staff));
        localStorage.setItem('staffRole', staff.role === 'Security Guard' ? 'security' : 'ticketing');
        
        switch (staff.role) {
          case 'Security Guard':
            navigate('/security-dashboard');
            break;
          case 'Ticket Counter':
            navigate('/staff-dashboard');
            break;
          default:
            navigate('/staff-dashboard');
        }
      } else {
        const { token, user } = response.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));

        switch (user.role) {
          case 'super_admin':
            navigate('/super-admin');
            break;
          case 'tenant_admin':
          case 'park_admin':
          case 'manager':
            navigate('/tenant-dashboard');
            break;
          case 'security':
            navigate('/security-dashboard');
            break;
          case 'ticketing_staff':
            navigate('/staff-dashboard');
            break;
          default:
            navigate('/');
        }
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
      display: 'flex',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Left Side - Branding */}
      <div style={{
        flex: 1,
        background: 'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }} />
        
        <div style={{ textAlign: 'center', zIndex: 2 }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            backdropFilter: 'blur(10px)'
          }}>
            <img 
              src="/Logo1.png" 
              alt="Tiket Jini" 
              style={{ width: '50px', height: '50px', borderRadius: '12px' }}
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
            />
            <span style={{ display: 'none', fontSize: '32px' }}>🎫</span>
          </div>
          
          <h1 style={{ 
            fontSize: '36px', 
            fontWeight: '300', 
            margin: '0 0 16px 0',
            letterSpacing: '-1px'
          }}>
            Tiket Jini
          </h1>
          
          <p style={{ 
            fontSize: '18px', 
            opacity: 0.9, 
            margin: '0 0 40px 0',
            fontWeight: '300'
          }}>
            Your gateway to amazing experiences
          </p>
          
          <div style={{
            background: 'rgba(255,255,255,0.1)',
            padding: '24px',
            borderRadius: '16px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '400' }}>
              Partner Portal
            </h3>
            <p style={{ margin: 0, opacity: 0.8, fontSize: '14px', lineHeight: '1.5' }}>
              Secure access for partners to manage bookings, sites, and customer experiences
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '400px',
          background: 'white',
          padding: '48px',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <h2 style={{ 
              fontSize: '28px', 
              fontWeight: '600', 
              color: '#1a1a1a',
              margin: '0 0 8px 0'
            }}>
              Welcome back
            </h2>
            <p style={{ 
              color: '#666', 
              fontSize: '16px',
              margin: '0 0 16px 0'
            }}>
              Sign in to your account
            </p>
            
            <div style={{
              display: 'flex',
              background: '#f8f9fa',
              borderRadius: '8px',
              padding: '4px',
              marginBottom: '24px'
            }}>
              <button
                type="button"
                onClick={() => setIsStaffLogin(false)}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  background: !isStaffLogin ? 'white' : 'transparent',
                  color: !isStaffLogin ? '#06b6d4' : '#666',
                  boxShadow: !isStaffLogin ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                }}
              >
                Admin Login
              </button>
              <button
                type="button"
                onClick={() => setIsStaffLogin(true)}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  background: isStaffLogin ? 'white' : 'transparent',
                  color: isStaffLogin ? '#06b6d4' : '#666',
                  boxShadow: isStaffLogin ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                }}
              >
                Staff Login
              </button>
            </div>
          </div>

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500', 
                color: '#333',
                fontSize: '14px'
              }}>
                {isStaffLogin ? 'Staff Email' : 'Email'}
              </label>
              <input
                type="email"
                value={credentials.email}
                onChange={(e) => setCredentials({...credentials, email: e.target.value})}
                placeholder={isStaffLogin ? 'Enter staff email' : 'Enter your email'}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  backgroundColor: '#fafafa'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#06b6d4';
                  e.target.style.backgroundColor = 'white';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e1e5e9';
                  e.target.style.backgroundColor = '#fafafa';
                }}
                required
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500', 
                color: '#333',
                fontSize: '14px'
              }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  placeholder={isStaffLogin ? 'Default: staff123' : 'Enter your password'}
                  style={{
                    width: '100%',
                    padding: '12px 40px 12px 16px',
                    border: '2px solid #e1e5e9',
                    borderRadius: '8px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    backgroundColor: '#fafafa'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.backgroundColor = 'white';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e1e5e9';
                    e.target.style.backgroundColor = '#fafafa';
                  }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#999'
                  }}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            {error && (
              <div style={{
                background: '#fef2f2',
                color: '#dc2626',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '14px',
                border: '1px solid #fecaca'
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: loading ? '#ccc' : 'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                marginBottom: '20px'
              }}
              onMouseOver={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 8px 25px rgba(6, 182, 212, 0.3)';
                }
              }}
              onMouseOut={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>

            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '14px'
            }}>
              <button
                type="button"
                onClick={() => navigate('/')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#06b6d4',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                ← Back to Home
              </button>
              
              <button
                type="button"
                onClick={() => alert('Contact admin for password reset')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Forgot password?
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;